import { useMemo, useEffect } from "react";

import { ChartColumn } from "lucide-react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchGuidanceCadenceSchedule,
  fetchGuidanceHubEnabled,
  fetchGuidanceNavLinks,
  fetchGuidancePlaybookLinks,
  fetchGuidanceRhythm,
} from "../../Store/Reducers/GuidanceSlice";
import {
  fetchOrganizationSettings,
  fetchPortfolioMapsConfig,
  selectOrganizationSettingsAccess,
} from "../../Store/Reducers/VendorAdminSlice";
import {
  filterMenuItemsForRelationshipAccess,
  getMenuItemsForRole,
  isRole,
  ROLES,
  getDefaultRouteForRole,
  RELATIONSHIP_ROUTE_PATH,
} from "../../Utils/Constants/RoleConstants";
import { isGuidanceHubEnabledForRole } from "../../Utils/Guidance/guidanceHubSettings";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";
import GuidancePreviewBanner from "../GuidancePlaybooks/GuidancePreviewBanner";
import GuideDrawer from "../GuidancePlaybooks/GuideDrawer";
import Sidebar from "../Sidebar";

import "./style.scss";

interface RoleBasedLayoutProps {
  executiveOps?: boolean;
}

const RoleBasedLayout = ({ executiveOps = false }: RoleBasedLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector(state => state.authentication);
  const { resolved, enabled, error } = useAppSelector(
    selectOrganizationSettingsAccess
  );
  const { hubEnabled, hubEnabledRoles, hubEnabledLoaded, previewDayOverride } =
    useAppSelector(state => state.guidance);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userRole = user?.user?.role || loggedInUser?.role;
  const tenantSlug = loggedInUser?.slug || "";

  useEffect(() => {
    if (!isRole(userRole, ROLES.IZBA_ADMIN)) {
      dispatch(fetchOrganizationSettings());
      dispatch(fetchPortfolioMapsConfig());
    }
  }, [dispatch, userRole]);

  useEffect(() => {
    if (!resolved || error || enabled) return;
    if (!location.pathname.endsWith(RELATIONSHIP_ROUTE_PATH)) return;

    const defaultRoute = getDefaultRouteForRole(userRole);
    const targetPath =
      tenantSlug && !isRole(userRole, ROLES.IZBA_ADMIN)
        ? `/${tenantSlug}${defaultRoute}`
        : defaultRoute;
    navigate(targetPath, { replace: true });
  }, [
    enabled,
    error,
    location.pathname,
    navigate,
    resolved,
    tenantSlug,
    userRole,
  ]);

  useEffect(() => {
    dispatch(fetchGuidanceNavLinks());
    dispatch(fetchGuidancePlaybookLinks());
    dispatch(fetchGuidanceRhythm());
    dispatch(fetchGuidanceHubEnabled());
    dispatch(fetchGuidanceCadenceSchedule());
  }, [dispatch]);

  const menuItems = useMemo(() => {
    let items = getMenuItemsForRole(userRole);

    const hasOps = items.some(i => i.path === "/ops");

    if (!hasOps && executiveOps) {
      const execOpsItem = {
        name: "Exec Ops",
        path: "/ops",
        lucideIcon: ChartColumn,
      };

      const homeIdx = items.findIndex(i => i.path === "/home");
      const guidanceIdx = items.findIndex(
        i => i.path === "/guidance/playbooks" || i.path === "/guidance-library"
      );

      const insertAt =
        guidanceIdx >= 0 ? guidanceIdx + 1 : homeIdx >= 0 ? homeIdx + 1 : 0;

      items = [
        ...items.slice(0, insertAt),
        execOpsItem,
        ...items.slice(insertAt),
      ];
    }

    return filterMenuItemsForRelationshipAccess(items, resolved, enabled);
  }, [userRole, executiveOps, resolved, enabled]);

  const isAdminPage =
    location.pathname.startsWith("/izba-admin") ||
    (location.pathname === "/" && isRole(userRole, ROLES.IZBA_ADMIN));

  const showGuideDrawer =
    !isAdminPage &&
    hubEnabledLoaded &&
    hubEnabled &&
    isGuidanceHubEnabledForRole(userRole, hubEnabledRoles) &&
    !isRole(userRole, ROLES.IZBA_ADMIN) &&
    !isRole(userRole, ROLES.VENDOR_ADMIN);

  return (
    <>
      {previewDayOverride !== null && <GuidancePreviewBanner />}
      <div
        className={
          isAdminPage
            ? "bg-gray-50 h-screen h-full"
            : "layout-container  bg-gray-100"
        }
      >
        {!isAdminPage && <Sidebar menuItems={menuItems} />}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
          className={isAdminPage ? "h-full" : ""}
        >
          <div
            style={{
              flex: 1,
              padding: isAdminPage ? "0" : "0 22px",
              overflowY: "auto",
            }}
          >
            <Outlet />
          </div>
        </div>
      </div>

      {showGuideDrawer && <GuideDrawer userRole={userRole} />}
    </>
  );
};

export default RoleBasedLayout;
